import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EntryLog } from '../../../../models/member-logs/entry-log';
import { MemberDetailsService } from '../../../../services/member-details.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { dateFormatHour } from '../../../utilities/constants';
import { DatePipe } from '@angular/common';
import { TranslationEnum } from '../../../../../assets/i18n/translation-enum';
import { TranslateService } from '@ngx-translate/core';
import {
  PeriodPreset,
  periodRange,
  PeriodRange,
} from '../../../utilities/period-range';

@Component({
  selector: 'app-check-in-history',
  templateUrl: './check-in-history.component.html',
  styleUrls: ['./check-in-history.component.scss'],
})
export class CheckInHistoryComponent implements OnInit, OnDestroy {
  @Input() memberId: string;
  @Input() locationId: string;

  entries: EntryLog[] = [];
  totalRecords = 0;
  loading = false;
  dateFormatHour = dateFormatHour;
  TranslationEnum = TranslationEnum;

  /**
   * Defaults to the whole history, which is what this tab showed before it
   * could be filtered - narrowing is then the user's choice rather than
   * something that silently hides entries on open.
   */
  period: PeriodPreset = 'all';
  periods: { label: string; value: PeriodPreset }[];

  private pageNumber = 1;
  private readonly pageSize = 10;
  private destroy$ = new Subject<boolean>();

  constructor(
    private memberDetailsService: MemberDetailsService,
    private translateService: TranslateService,
    public datePipe: DatePipe
  ) {
    const label = (key: string) => this.translateService.instant(key);
    this.periods = [
      { label: label(TranslationEnum.Common.PERIOD_ALL), value: 'all' },
      {
        label: label(TranslationEnum.Common.PERIOD_THIS_MONTH),
        value: 'thisMonth',
      },
      {
        label: label(TranslationEnum.Common.PERIOD_LAST_MONTH),
        value: 'lastMonth',
      },
      {
        label: label(TranslationEnum.Common.PERIOD_LAST_3_MONTHS),
        value: 'last3Months',
      },
      {
        label: label(TranslationEnum.Common.PERIOD_LAST_6_MONTHS),
        value: 'last6Months',
      },
      {
        label: label(TranslationEnum.Common.PERIOD_THIS_YEAR),
        value: 'thisYear',
      },
      {
        label: label(TranslationEnum.Common.PERIOD_LAST_YEAR),
        value: 'lastYear',
      },
    ];
  }

  ngOnInit(): void {
    this.reload();
  }

  get canLoadMore(): boolean {
    return !this.loading && this.entries.length < this.totalRecords;
  }

  onPeriodChange(period: PeriodPreset): void {
    this.period = period;
    this.reload();
  }

  loadMore(): void {
    this.pageNumber++;
    this.fetch();
  }

  downloadReport(): void {
    this.memberDetailsService
      .downloadMemberEntriesLogs(
        this.memberId,
        this.locationId,
        this.currentRange()
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: Blob) => {
        if (!data) {
          return;
        }
        const blob = new Blob([data], { type: 'text/csv' });
        const currentDate = this.datePipe.transform(Date.now(), 'yyyy-MM-dd');
        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = `report-entries-logs-${this.memberId}-${currentDate}.csv`;
        link.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );

        setTimeout(() => {
          window.URL.revokeObjectURL(downloadURL);
          link.remove();
        }, 100);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private reload(): void {
    this.pageNumber = 1;
    this.entries = [];
    this.totalRecords = 0;
    this.fetch();
  }

  private fetch(): void {
    if (!this.memberId || !this.locationId) {
      return;
    }
    this.loading = true;
    const range = this.currentRange();

    this.memberDetailsService
      .getEntityLogs(
        'entries',
        this.memberId,
        this.locationId,
        this.pageNumber,
        this.pageSize,
        range.from || range.to
          ? { startDate: range.from, endDate: range.to }
          : null
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        // pages are appended, so "load more" keeps what is already on screen
        this.entries = [...this.entries, ...(result?.data || [])];
        this.totalRecords = result?.totalRecords || 0;
        this.loading = false;
      });
  }

  private currentRange(): PeriodRange {
    return periodRange(this.period);
  }
}
